import { formatISO } from 'date-fns';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { BookingDataEnum } from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import {
  BookingConfig,
  BookingConfigEnum,
  BookingStep,
  BookingStepEnum,
  BookingStepValueEnum,
  BookingSubStep,
  BookingSubStepEnum,
  BookingSubStepOption,
  BookingSubStepOptionEnum,
  BookingSubStepValueEnum,
} from '../../components/BookingWidget/bookingSteps.interface';
import contextDefaultValues from '../../context/bookingContext/bookingContext.const';
import {
  BookingStepsContextState,
  BookingSubStepOptionType,
  BookingSubStepsQuotationForm,
} from '../../context/bookingContext/bookingContext.types';
import { getAgeRange } from '../../components/QuotationForm/components/TravellersAgeTiles/travellers-age.const';
import {
  AppBootstrapEnum,
  PossibleValuesInUrl,
} from '../consts/appBootstrap.const';
import {
  insertOptionsIntoObject,
  insertAddonsIntoObject,
  prepareAddons,
} from './insuranceOptions';
import { setCookie } from './cookies.helper';
import getUrlSearchParam from './urlSearchParams.helper';
import { TravellerAgeAndBirthdate } from '../../components/QuotationFormWeb/quotationFormWeb.types';
import { getTravellerAgeFromDate } from './getTravellersAge';
import { ApiDateFormat, UrlDateFormat } from '../consts/app.const';
import { CacheDataContext } from '../../context/cacheContext/cacheContext';

dayjs.extend(customParseFormat);

const getActiveCardConfig = (
  cards: BookingSubStep[],
  cardKey: BookingSubStepValueEnum,
): BookingSubStep =>
  cards.filter(
    (card: BookingSubStep): boolean =>
      card[BookingSubStepEnum.KeyName] === cardKey,
  )[0];

const getBootstrapParamsFromUrl = (
  bookingConfig: BookingConfig,
  bookingSteps: BookingStepsContextState,
  setBookingReferenceId?: (id: string) => void,
): BookingStepsContextState => {
  const searchParams: URLSearchParams = new URLSearchParams(
    window.location.search,
  );
  const lowerCaseSearchParams: URLSearchParams = new URLSearchParams();
  Array.from(searchParams.entries()).forEach(([keyT, valueT]): void =>
    lowerCaseSearchParams.append(keyT.toLowerCase(), valueT),
  );
  const extractedParameters: { [key: string]: string } = {};
  PossibleValuesInUrl.forEach((param: BookingDataEnum | AppBootstrapEnum) => {
    const value: string | null = lowerCaseSearchParams.get(param.toLowerCase());
    if (value !== null) {
      extractedParameters[param] = value;
    }
  });

  const defaultValuesBookingSteps: BookingStepsContextState =
    contextDefaultValues.bookingSteps;

  const bookingConfigForm: BookingStep = bookingConfig[
    BookingConfigEnum.BookingSteps
  ].filter(
    (step: BookingStep): boolean =>
      step[BookingStepEnum.KeyName] === BookingStepValueEnum.QuotationForm,
  )[0];

  const bookingConfigProposals: BookingStep = bookingConfig[
    BookingConfigEnum.BookingSteps
  ].filter(
    (step: BookingStep): boolean =>
      step[BookingStepEnum.KeyName] === BookingStepValueEnum.QuotationProposals,
  )[0];

  if (Object.keys(extractedParameters).length > 0) {
    if (extractedParameters[BookingDataEnum.CurrentStep]) {
      defaultValuesBookingSteps[BookingStepValueEnum.CurrentStep] =
        extractedParameters[BookingDataEnum.CurrentStep];
    }

    if (extractedParameters[BookingDataEnum.Partner]) {
      defaultValuesBookingSteps[BookingStepValueEnum.Partner] =
        extractedParameters[BookingDataEnum.Partner];
    }

    if (extractedParameters[BookingDataEnum.AggregatorId]) {
      defaultValuesBookingSteps[BookingStepValueEnum.AggregatorId] =
        extractedParameters[BookingDataEnum.AggregatorId];
    }

    if (extractedParameters[BookingDataEnum.AffiliateId]) {
      defaultValuesBookingSteps[BookingStepValueEnum.AffiliateId] =
        extractedParameters[BookingDataEnum.AffiliateId];
      setCookie(
        'affiliateId',
        extractedParameters[BookingDataEnum.AffiliateId],
        30,
      );
    }

    if (extractedParameters[BookingDataEnum.Catalog]) {
      const activeCard: BookingSubStep = getActiveCardConfig(
        bookingConfigForm[BookingStepEnum.Cards],
        BookingSubStepValueEnum.TripDetails,
      );
      const options: BookingSubStepOption[] = activeCard[
        BookingSubStepEnum.Options
      ] as BookingSubStepOption[];
      const selectedValue = options.filter(
        (option: BookingSubStepOption) =>
          option[BookingSubStepOptionEnum.Value] ===
          extractedParameters[BookingDataEnum.Catalog],
      );
      if (selectedValue.length > 0) {
        // eslint-disable-next-line no-param-reassign
        bookingSteps[BookingStepValueEnum.QuotationForm][
          BookingSubStepValueEnum.TripDetails
        ] = {
          [BookingSubStepOptionEnum.Value]:
            selectedValue[0][BookingSubStepOptionEnum.Value],
          [BookingSubStepOptionEnum.Label]:
            selectedValue[0][BookingSubStepOptionEnum.Label],
        };
      }
    }

    if (extractedParameters[BookingDataEnum.StartDate]) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationForm][
        BookingSubStepValueEnum.DepartureDate
      ] = formatISO(new Date(extractedParameters[BookingDataEnum.StartDate]));
    }

    if (extractedParameters[BookingDataEnum.EndDate]) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationForm][
        BookingSubStepValueEnum.ReturnDate
      ] = formatISO(new Date(extractedParameters[BookingDataEnum.EndDate]));
    }

    if (extractedParameters[BookingDataEnum.Destination]) {
      const activeCard: BookingSubStep = getActiveCardConfig(
        bookingConfigForm[BookingStepEnum.Cards],
        BookingSubStepValueEnum.GeographicalZone,
      );
      const options: BookingSubStepOption[] = activeCard[
        BookingSubStepEnum.Options
      ] as BookingSubStepOption[];

      const destinations = extractedParameters[BookingDataEnum.Destination]
        .split(',')
        .map((destination: string) => destination.trim().toLowerCase());
      const destinationLabelsParam =
        lowerCaseSearchParams.get('destinationlabels');
      const destinationLabels = destinationLabelsParam?.length
        ? destinationLabelsParam
            .split(',')
            .map((label: string) => decodeURIComponent(label).trim())
        : [];

      const selectedValues: BookingSubStepOption[] = [];
      const seenDestinationValues = new Set<string>();
      destinations.forEach((destinationValue: string, index: number): void => {
        if (seenDestinationValues.has(destinationValue)) {
          return;
        }

        const preferredLabel = destinationLabels[index]?.toLowerCase();
        const matchingOptions = options.filter(
          (option: BookingSubStepOption): boolean =>
            option[BookingSubStepOptionEnum.Value].toLowerCase() ===
            destinationValue,
        );
        const selectedOption =
          matchingOptions.find(
            (option: BookingSubStepOption): boolean =>
              !!preferredLabel &&
              option[BookingSubStepOptionEnum.Label].toLowerCase() ===
                preferredLabel,
          ) || matchingOptions[0];

        if (selectedOption) {
          seenDestinationValues.add(destinationValue);
          selectedValues.push(selectedOption);
        }
      });

      if (selectedValues.length > 0) {
        defaultValuesBookingSteps[BookingStepValueEnum.QuotationForm][
          BookingSubStepValueEnum.GeographicalZone
        ] = selectedValues.map((selectedValue) => ({
          [BookingSubStepOptionEnum.Value]:
            selectedValue[BookingSubStepOptionEnum.Value],
          [BookingSubStepOptionEnum.Label]:
            selectedValue[BookingSubStepOptionEnum.Label],
        }));
      }
    }

    if (extractedParameters[BookingDataEnum.MaxTripDuration]) {
      const activeCard: BookingSubStep = getActiveCardConfig(
        bookingConfigForm[BookingStepEnum.Cards],
        BookingSubStepValueEnum.MaxTripDuration,
      );
      const options: BookingSubStepOption[] = activeCard[
        BookingSubStepEnum.Options
      ] as BookingSubStepOption[];
      const selectedValue: BookingSubStepOption[] = options.filter(
        (option: BookingSubStepOption): boolean =>
          option[BookingSubStepOptionEnum.Value].toLowerCase() ===
          extractedParameters[BookingDataEnum.MaxTripDuration].toLowerCase(),
      );
      if (selectedValue.length > 0) {
        defaultValuesBookingSteps[BookingStepValueEnum.QuotationForm][
          BookingSubStepValueEnum.MaxTripDuration
        ] = {
          [BookingSubStepOptionEnum.Value]:
            selectedValue[0][BookingSubStepOptionEnum.Value],
          [BookingSubStepOptionEnum.Label]:
            selectedValue[0][BookingSubStepOptionEnum.Label],
        };
      }
    }

    if (extractedParameters[BookingDataEnum.PromoCode]) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationForm][
        BookingSubStepValueEnum.PromoCode
      ] = extractedParameters[BookingDataEnum.PromoCode];
      if (extractedParameters[BookingDataEnum.AffiliateId]) {
        setCookie(
          BookingDataEnum.PromoCode,
          extractedParameters[BookingDataEnum.PromoCode],
          30,
        );
      }
    }

    const travellersAge: TravellerAgeAndBirthdate[] = [];
    for (let i = 1; i <= 10; i += 1) {
      if (extractedParameters[`companion${i}`]) {
        const age = extractedParameters[`companion${i}`];
        if (bookingConfig[BookingConfigEnum.Layout] === 'WEB') {
          // check age is date string
          let parsedDOB = dayjs(age, UrlDateFormat, true);

          if (!parsedDOB.isValid()) {
            parsedDOB = dayjs(age, ApiDateFormat, true);
          }

          if (parsedDOB.isValid()) {
            travellersAge.push({
              age: getTravellerAgeFromDate(parsedDOB).toString(),
              dateOfBirth: parsedDOB,
            });
          } else {
            travellersAge.push({
              age: extractedParameters[`companion${i}`],
            });
          }
        } else {
          const range = getAgeRange(extractedParameters[`companion${i}`]);
          if (range) {
            travellersAge.push({ age: range });
          }
        }
      }
    }

    if (travellersAge.length > 0) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationForm][
        BookingSubStepValueEnum.TravellersAge
      ] = travellersAge;
    }

    if (extractedParameters[BookingDataEnum.Product]) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationProposals][
        BookingSubStepValueEnum.Proposal
      ] = {
        ...defaultValuesBookingSteps[BookingStepValueEnum.QuotationProposals][
          BookingSubStepValueEnum.Proposal
        ],
        code: extractedParameters[BookingDataEnum.Product],
      };
    }

    if (setBookingReferenceId && extractedParameters.bookingReferenceId) {
      setBookingReferenceId(extractedParameters.bookingReferenceId);
    }

    if (extractedParameters[BookingDataEnum.Addons]) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationProposals][
        BookingSubStepValueEnum.Addons
      ] = insertAddonsIntoObject(extractedParameters[BookingDataEnum.Addons]);
    }

    if (extractedParameters[BookingDataEnum.Excess]) {
      defaultValuesBookingSteps[BookingStepValueEnum.QuotationProposals][
        BookingSubStepValueEnum.Excess
      ] = extractedParameters[BookingDataEnum.Excess] ?? '';
    }

    if (extractedParameters[BookingDataEnum.ExternalBookingId]) {
      defaultValuesBookingSteps[BookingStepValueEnum.ExtraFields][
        BookingDataEnum.ExternalBookingId
      ] = extractedParameters[BookingDataEnum.ExternalBookingId];
    }

    if (extractedParameters[BookingDataEnum.ExternalBacklink]) {
      defaultValuesBookingSteps[BookingStepValueEnum.ExtraFields][
        BookingDataEnum.ExternalBacklink
      ] = extractedParameters[BookingDataEnum.ExternalBacklink];
    }
  }
  return defaultValuesBookingSteps;
};

const createUrlFromBookingSteps = (
  quotationForm: BookingSubStepsQuotationForm,
  bookingConfig: BookingConfig,
  bookingSteps: BookingStepsContextState,
  bookingReferenceId?: string | null,
): string => {
  let urlString = `brand=${bookingConfig.brand}&${BookingDataEnum.CurrentStep}=${BookingStepValueEnum.QuotationProposals}`;
  urlString += `&${BookingDataEnum.Catalog}=${
    quotationForm[BookingSubStepValueEnum.TripDetails][
      BookingSubStepOptionEnum.Value
    ]
  }`;

  urlString += `&${BookingDataEnum.Destination}=${quotationForm[
    BookingSubStepValueEnum.GeographicalZone
  ]
    .map((value: BookingSubStepOptionType) =>
      value[BookingSubStepOptionEnum.Value].replace(/&/g, '%26'),
    )
    .join(',')}`;
  urlString += `&destinationLabels=${quotationForm[
    BookingSubStepValueEnum.GeographicalZone
  ]
    .map((value: BookingSubStepOptionType) =>
      encodeURIComponent(value[BookingSubStepOptionEnum.Label] || ''),
    )
    .join(',')}`;

  urlString += `&${BookingDataEnum.StartDate}=${
    quotationForm[BookingSubStepValueEnum.DepartureDate]
  }`;

  urlString += `&${BookingDataEnum.EndDate}=${
    quotationForm[BookingSubStepValueEnum.ReturnDate]
  }`;

  if (
    bookingSteps[BookingStepValueEnum.QuotationProposals][
      BookingSubStepValueEnum.Addons
    ]
  ) {
    urlString += `&${BookingDataEnum.Addons}=${prepareAddons(
      bookingSteps[BookingStepValueEnum.QuotationProposals][
        BookingSubStepValueEnum.Addons
      ],
    )}`;
  }

  if (quotationForm[BookingSubStepValueEnum.PromoCode]) {
    urlString += `&${BookingDataEnum.PromoCode}=${
      quotationForm[BookingSubStepValueEnum.PromoCode]
    }`;
  }

  const affiliateId = bookingSteps[BookingStepValueEnum.AffiliateId];
  if (affiliateId) {
    urlString += `&${BookingDataEnum.AffiliateId}=${affiliateId}`;
  }

  if (bookingReferenceId) {
    urlString += `&${AppBootstrapEnum.BookingReferenceId}=${bookingReferenceId}`;
  }

  const maxTripDuration =
    quotationForm[BookingSubStepValueEnum.MaxTripDuration]?.[
      BookingSubStepOptionEnum.Value
    ];
  if (maxTripDuration) {
    urlString += `&${BookingDataEnum.MaxTripDuration}=${maxTripDuration}`;
  }

  quotationForm[BookingSubStepValueEnum.TravellersAge].forEach(
    (
      travellerPersonalInformation: TravellerAgeAndBirthdate,
      index: number,
    ): void => {
      if (travellerPersonalInformation.dateOfBirth) {
        const encodedDOB = encodeURIComponent(
          travellerPersonalInformation.dateOfBirth.toString(),
        );
        urlString += `&companion${index + 1}=${encodedDOB}`;
      } else {
        urlString += `&companion${index + 1}=${
          travellerPersonalInformation.age
        }`;
      }
    },
  );
  return urlString;
};

export { getBootstrapParamsFromUrl, createUrlFromBookingSteps };
